import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Key as KeyIcon, ArrowRight } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var keyTypes = ["main_door", "room", "mailbox", "garage", "storage"];

var emptyForm = {
  key_label: "",
  key_type: "main_door",
  property_id: "",
  unit_id: "",
  copies_count: "1",
  current_holder: "",
  holder_contact: "",
  has_certificate: false,
  certificate_number: "",
  notes: "",
};

var emptyHandoverForm = {
  from_holder: "",
  to_holder: "",
  to_contact: "",
  handover_date: "",
  notes: "",
};

export default function Keys() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [keys, setKeys] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [filterUnits, setFilterUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  var [editingKey, setEditingKey] = useState(null);
  var [selectedKey, setSelectedKey] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [handoverForm, setHandoverForm] = useState(emptyHandoverForm);
  var [search, setSearch] = useState("");
  var [filterProperty, setFilterProperty] = useState("all");
  var [filterUnit, setFilterUnit] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { fetchData(); }, []);

  // Fetch units when form property changes (for dialog)
  useEffect(function() {
    if (form.property_id && form.property_id !== "none") {
      fetchUnits(form.property_id);
    } else {
      setUnits([]);
      setForm(function(f) { return { ...f, unit_id: "" }; });
    }
  }, [form.property_id]);

  // Fetch units when filter property changes
  useEffect(function() {
    if (filterProperty && filterProperty !== "all") {
      fetchFilterUnits(filterProperty);
    } else {
      setFilterUnits([]);
      setFilterUnit("all");
    }
  }, [filterProperty]);

  async function fetchData() {
    try {
      var keysRes = await fetch(API_URL + "/api/keys", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var propertiesRes = await fetch(API_URL + "/api/properties", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (keysRes.ok) setKeys(await keysRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function fetchFilterUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setFilterUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching filter units:", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingKey ? API_URL + "/api/keys/" + editingKey.key_id : API_URL + "/api/keys";
      var method = editingKey ? "PUT" : "POST";

      var payload = {
        key_label: form.key_label,
        key_type: form.key_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        copies_count: parseInt(form.copies_count),
        current_holder: form.current_holder || null,
        holder_contact: form.holder_contact || null,
        has_certificate: form.has_certificate,
        certificate_number: form.certificate_number || null,
        notes: form.notes || null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingKey ? t("common.success") : t("common.success"));
        setDialogOpen(false);
        setEditingKey(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  async function handleHandover(e) {
    e.preventDefault();
    if (!selectedKey) return;

    try {
      var response = await fetch(API_URL + "/api/keys/" + selectedKey.key_id + "/handover", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(handoverForm),
      });

      if (response.ok) {
        toast.success(t("common.success"));
        setHandoverDialogOpen(false);
        setSelectedKey(null);
        setHandoverForm(emptyHandoverForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(key) {
    setEditingKey(key);
    setForm({
      key_label: key.key_label || "",
      key_type: key.key_type || "main_door",
      property_id: key.property_id || "",
      unit_id: key.unit_id || "",
      copies_count: key.copies_count?.toString() || "1",
      current_holder: key.current_holder || "",
      holder_contact: key.holder_contact || "",
      has_certificate: key.has_certificate || false,
      certificate_number: key.certificate_number || "",
      notes: key.notes || "",
    });
    // Fetch units for the selected property
    if (key.property_id) {
      fetchUnits(key.property_id);
    }
    setDialogOpen(true);
  }

  async function handleDelete(keyId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/keys/" + keyId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchData();
      } else {
        toast.error(t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function openHandoverDialog(key) {
    setSelectedKey(key);
    setHandoverForm({
      from_holder: key.current_holder || "",
      to_holder: "",
      to_contact: "",
      handover_date: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setHandoverDialogOpen(true);
  }

  function getPropertyName(propertyId) {
    if (!propertyId) return "-";
    for (var i = 0; i < properties.length; i++) {
      if (properties[i].property_id === propertyId) return properties[i].name;
    }
    return "-";
  }

  function getUnitName(unitId) {
    if (!unitId) return null;
    for (var i = 0; i < units.length; i++) {
      if (units[i].unit_id === unitId) return units[i].name;
    }
    return unitId.substring(0, 8) + "...";
  }

  function getFilteredKeys() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var matchLabel = k.key_label && k.key_label.toLowerCase().indexOf(searchLower) >= 0;
      var matchHolder = k.current_holder && k.current_holder.toLowerCase().indexOf(searchLower) >= 0;
      var matchSearch = matchLabel || matchHolder;
      var matchProperty = filterProperty === "all" || k.property_id === filterProperty;
      var matchUnit = filterUnit === "all" || k.unit_id === filterUnit;
      if (matchSearch && matchProperty && matchUnit) result.push(k);
    }
    return result;
  }

  function renderFilterPropertyOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderFilterUnitOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < filterUnits.length; i++) {
      items.push(<SelectItem key={filterUnits[i].unit_id} value={filterUnits[i].unit_id}>{filterUnits[i].name}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderKeyTypeOptions() {
    var items = [];
    for (var i = 0; i < keyTypes.length; i++) {
      items.push(<SelectItem key={keyTypes[i]} value={keyTypes[i]}>{t("keys.types." + keyTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderKeyCards() {
    var filteredKeys = getFilteredKeys();
    var cards = [];
    for (var i = 0; i < filteredKeys.length; i++) {
      var key = filteredKeys[i];
      var keyId = key.key_id;
      var keyData = JSON.parse(JSON.stringify(key));
      cards.push(
        <Card key={keyId} className="bento-card" data-testid={"key-card-" + keyId}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <KeyIcon className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">{key.key_label}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{t("keys.types." + key.key_type)}</Badge>
                    <span className="text-xs text-muted-foreground">{key.copies_count} {t("keys.copies")}</span>
                    {key.unit_id && <Badge variant="secondary" className="text-xs">{getUnitName(key.unit_id)}</Badge>}
                  </div>
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, keyData)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, keyId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t("assets.property")}:</span>
                <p className="font-medium">{getPropertyName(key.property_id)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">{t("keys.currentHolder")}:</span>
                <p className="font-medium">{key.current_holder || t("common.noData")}</p>
              </div>
            </div>

            {key.has_certificate && (
              <div className="flex items-center gap-2 text-sm p-2 rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <Badge variant="outline" className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-300">{t("keys.hasCertificate")}</Badge>
                {key.certificate_number && <span className="text-muted-foreground">#{key.certificate_number}</span>}
              </div>
            )}

            {canEdit && (
              <Button variant="outline" className="w-full" onClick={openHandoverDialog.bind(null, keyData)} data-testid={"handover-btn-" + keyId}>
                <ArrowRight className="w-4 h-4 mr-2" />
                {t("keys.recordHandover")}
              </Button>
            )}

            {key.handover_history && key.handover_history.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("keys.handoverHistory")}</p>
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {renderHandoverHistory(key.handover_history)}
                  </div>
                </ScrollArea>
              </div>
            )}

            {isAdmin && key.owner_name && (
              <div className="pt-3 border-t">
                <div className="text-xs text-muted-foreground">{t("common.owner")}</div>
                <div className="text-sm font-medium truncate">{key.owner_name}</div>
                <div className="text-xs text-muted-foreground truncate">{key.owner_email}</div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }
    return cards;
  }

  function renderHandoverHistory(history) {
    var items = [];
    var reversed = history.slice().reverse();
    for (var i = 0; i < reversed.length; i++) {
      var h = reversed[i];
      items.push(
        <div key={i} className="p-2 rounded bg-muted/50 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{h.from_holder || "N/A"}</span>
            <ArrowRight className="w-3 h-3" />
            <span className="font-medium">{h.to_holder}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{h.handover_date}</p>
        </div>
      );
    }
    return items;
  }

  var filteredKeys = getFilteredKeys();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="keys-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("keys.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("keys.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingKey(null); setForm(emptyForm); setUnits([]); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-key-btn"><Plus className="w-4 h-4 mr-2" />{t("keys.addKey")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingKey ? t("keys.editKey") : t("keys.addNewKey")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("keys.keyLabel")}</Label>
                  <Input value={form.key_label} onChange={function(e) { setForm({ ...form, key_label: e.target.value }); }} placeholder="e.g., Main Entrance - Building A" required data-testid="key-label-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("keys.keyType")}</Label>
                    <Select value={form.key_type} onValueChange={function(v) { setForm({ ...form, key_type: v }); }}>
                      <SelectTrigger data-testid="key-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderKeyTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("keys.copies")}</Label>
                    <Input type="number" min="1" value={form.copies_count} onChange={function(e) { setForm({ ...form, copies_count: e.target.value }); }} data-testid="key-copies-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({ ...form, property_id: v, unit_id: "" }); }}>
                      <SelectTrigger data-testid="key-property-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("common.unit")}</Label>
                    <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({ ...form, unit_id: v }); }} disabled={!form.property_id || form.property_id === "none" || units.length === 0}>
                      <SelectTrigger data-testid="key-unit-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderUnitOptions()}</SelectContent>
                    </Select>
                    {form.property_id && form.property_id !== "none" && units.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">{t("common.noUnitsAvailable")}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("keys.currentHolder")}</Label>
                    <Input value={form.current_holder} onChange={function(e) { setForm({ ...form, current_holder: e.target.value }); }} placeholder="Holder name" data-testid="key-holder-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("keys.holderContact")}</Label>
                    <Input value={form.holder_contact} onChange={function(e) { setForm({ ...form, holder_contact: e.target.value }); }} placeholder="Phone/Email" data-testid="key-holder-contact-input" />
                  </div>
                </div>
                <div className="flex items-center gap-4 p-3 rounded border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="has_certificate" 
                      checked={form.has_certificate} 
                      onChange={function(e) { setForm({...form, has_certificate: e.target.checked}); }}
                      className="w-4 h-4"
                      data-testid="key-has-certificate-checkbox"
                    />
                    <Label htmlFor="has_certificate" className="cursor-pointer text-sm">{t("keys.hasCertificate")}</Label>
                  </div>
                  {form.has_certificate && (
                    <div className="flex-1">
                      <Input 
                        value={form.certificate_number} 
                        onChange={function(e) { setForm({ ...form, certificate_number: e.target.value }); }} 
                        placeholder={t("keys.certificateNumber")} 
                        data-testid="key-certificate-number-input" 
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label className="form-label">{t("common.notes")}</Label>
                  <Textarea value={form.notes} onChange={function(e) { setForm({ ...form, notes: e.target.value }); }} placeholder={t("common.notes")} rows={2} data-testid="key-notes-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="key-submit-btn">{editingKey ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Handover Dialog */}
      <Dialog open={handoverDialogOpen} onOpenChange={function(open) { setHandoverDialogOpen(open); if (!open) { setSelectedKey(null); setHandoverForm(emptyHandoverForm); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("keys.recordHandover")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHandover} className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <strong>{t("keys.keyLabel")}:</strong> {selectedKey?.key_label}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">{t("keys.from")}</Label>
                <Input value={handoverForm.from_holder} onChange={function(e) { setHandoverForm({ ...handoverForm, from_holder: e.target.value }); }} placeholder="Previous holder" data-testid="handover-from-input" />
              </div>
              <div>
                <Label className="form-label">{t("keys.to")}</Label>
                <Input value={handoverForm.to_holder} onChange={function(e) { setHandoverForm({ ...handoverForm, to_holder: e.target.value }); }} placeholder="New holder" required data-testid="handover-to-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">{t("keys.holderContact")}</Label>
                <Input value={handoverForm.to_contact} onChange={function(e) { setHandoverForm({ ...handoverForm, to_contact: e.target.value }); }} placeholder="Phone/Email" data-testid="handover-contact-input" />
              </div>
              <div>
                <Label className="form-label">{t("keys.handoverDate")}</Label>
                <Input type="date" value={handoverForm.handover_date} onChange={function(e) { setHandoverForm({ ...handoverForm, handover_date: e.target.value }); }} required data-testid="handover-date-input" />
              </div>
            </div>
            <div>
              <Label className="form-label">{t("common.notes")}</Label>
              <Textarea value={handoverForm.notes} onChange={function(e) { setHandoverForm({ ...handoverForm, notes: e.target.value }); }} placeholder={t("common.notes")} rows={2} data-testid="handover-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={function() { setHandoverDialogOpen(false); }}>{t("common.cancel")}</Button>
              <Button type="submit" data-testid="handover-submit-btn">{t("keys.recordHandover")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="key-search-input" />
        </div>
        <Select value={filterProperty} onValueChange={function(v) { setFilterProperty(v); setFilterUnit("all"); }}>
          <SelectTrigger className="w-48" data-testid="key-filter-property"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
          <SelectContent>{renderFilterPropertyOptions()}</SelectContent>
        </Select>
        <Select value={filterUnit} onValueChange={setFilterUnit} disabled={filterProperty === "all" || filterUnits.length === 0}>
          <SelectTrigger className="w-48" data-testid="key-filter-unit"><SelectValue placeholder={t("common.unit")} /></SelectTrigger>
          <SelectContent>{renderFilterUnitOptions()}</SelectContent>
        </Select>
      </div>

      {filteredKeys.length > 0 ? (
        <div className="grid lg:grid-cols-2 gap-6">{renderKeyCards()}</div>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <KeyIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("keys.noKeys")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("keys.addFirstKey")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
